import { Component } from '@theme/component';
import { lockScroll, unlockScroll, isClickedOutside } from '@theme/utilities';
import { getScrollTop } from '@theme/scroll-container';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';
import { StandardEvents } from '@shopify/events';

class CollectiveHeader extends Component {
  requiredRefs = ['menu', 'menuOpener', 'cartCount', 'cartTrigger', 'liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    this.bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.bind();
  }

  bind() {
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    const syncScroll = () => this.classList.toggle('is-scrolled', getScrollTop() > 8);
    document.addEventListener('scroll', syncScroll, { capture: true, passive: true, signal });
    window.addEventListener('resize', syncScroll, { passive: true, signal });
    window.addEventListener('pageshow', syncScroll, { signal });
    syncScroll();
    this.refs.menu.addEventListener('close', () => {
      unlockScroll(this.refs.menu);
      this.refs.menuOpener.setAttribute('aria-expanded', 'false');
      if (this.restoreMenuFocus && this.refs.menuOpener.isConnected) this.refs.menuOpener.focus({ preventScroll: true });
    }, { signal });
    this.refs.menu.addEventListener('click', event => {
      if (isClickedOutside(event, this.refs.menu)) this.closeMenu();
    }, { signal });
    const syncCart = () => {
      if (this.refs.cartTrigger.hasAttribute('aria-expanded')) {
        this.refs.cartTrigger.setAttribute('aria-expanded', document.getElementById('cart-drawer')?.hasAttribute('open') ? 'true' : 'false');
      }
    };
    document.addEventListener(DrawerOpenEvent.eventName, syncCart, { signal });
    document.addEventListener(DrawerCloseEvent.eventName, syncCart, { signal });
    syncCart();
    document.addEventListener(StandardEvents.cartLinesUpdate, async event => {
      try {
        const result = await event.promise;
        if (signal.aborted) return;
        const count = result?.cart?.totalQuantity ?? result?.detail?.itemCount;
        if (typeof count !== 'number') return;
        this.refs.cartCount.textContent = String(count);
        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${count}`;
      } catch (error) {
        if (error?.name !== 'AbortError') console.warn('[collective-header] Cart update failed:', error);
      }
    }, { signal });
  }

  openMenu() {
    if (this.refs.menu.open) return;
    this.restoreMenuFocus = true;
    this.refs.menu.showModal();
    lockScroll(this.refs.menu);
    this.refs.menuOpener.setAttribute('aria-expanded', 'true');
  }

  closeMenu(restoreFocus = true) {
    this.restoreMenuFocus = restoreFocus;
    if (!this.refs.menu.open) return;
    unlockScroll(this.refs.menu);
    this.refs.menu.close();
  }

  openSearch(event) {
    const search = document.getElementById('search-modal');
    if (typeof search?.showDialog !== 'function') return;
    event.preventDefault();
    this.closeMenu(false);
    search.showDialog();
  }

  openCart(event) {
    const cart = document.getElementById('cart-drawer');
    if (typeof cart?.toggle !== 'function') return;
    event.preventDefault();
    cart.toggle();
  }

  disconnectedCallback() {
    this.closeMenu(false);
    this.abort?.abort();
    unlockScroll(this.refs.menu);
    super.disconnectedCallback();
  }
}

if (!customElements.get('jsc-header')) customElements.define('jsc-header', CollectiveHeader);
